import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ModusButtonModule,
  ModusIconModule,
  ModusSwitchModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { ClassificationScheme } from '../../../classification/classification-scheme.model';
import { ColorPickerComponent } from '../../../color-picker/color-picker.component';
import { Scan3dStyle } from '../../models/scan-3d-style';
import { Scan3dIntensityComponent } from '../scan-3d-intensity/scan-3d-intensity.component';
import { scan3dGlobalClassificationTranslations } from './scan-3d-global-classification.translations';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-global-classification',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusSwitchModule,
    ModusButtonModule,
    ModusTooltipModule,
    ModusIconModule,
    ColorPickerComponent,
    Scan3dIntensityComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scan-3d-global-classification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dGlobalClassificationComponent {
  readonly scan3dStyle = input.required<Scan3dStyle>();

  readonly styleChange = output<Partial<Scan3dStyle>>();

  readonly classificationSchemes = computed(() => this.scan3dStyle().classificationSchemes);

  readonly blendIntensityControl = new FormControl<boolean>(false);
  readonly showIntensitySettings = computed(() => this.scan3dStyle().blendIntensity);

  readonly translations = scan3dGlobalClassificationTranslations();

  constructor() {
    this.subscribeToScan3dStyle();
    this.subscribeToFormValueChanges();
  }

  changeVisibility(classificationScheme: ClassificationScheme) {
    classificationScheme.visible = !classificationScheme.visible;

    const schemes = this.getClassificationSchemeChanges(classificationScheme);
    this.styleChange.emit({ classificationSchemes: schemes });
  }

  changeColor(color: string, classificationScheme: ClassificationScheme) {
    classificationScheme.rgba = color.replace('#', '').toUpperCase();

    const schemes = this.getClassificationSchemeChanges(classificationScheme);
    this.styleChange.emit({ classificationSchemes: schemes });
  }

  onStyleChange(style: Partial<Scan3dStyle>) {
    this.styleChange.emit(style);
  }

  private subscribeToScan3dStyle() {
    toObservable(this.scan3dStyle)
      .pipe(untilDestroyed(this))
      .subscribe((style) =>
        this.blendIntensityControl.setValue(style.blendIntensity, { emitEvent: false }),
      );
  }

  private subscribeToFormValueChanges() {
    this.blendIntensityControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((blendIntensity) =>
        this.styleChange.emit({ blendIntensity: blendIntensity ?? false }),
      );
  }

  private getClassificationSchemeChanges(classificationScheme: ClassificationScheme) {
    return this.classificationSchemes().map((scheme) =>
      scheme.id === classificationScheme.id ? classificationScheme : scheme,
    );
  }
}
